import { IMenuItem } from '../../Common';
import cc from 'classcat';
import { useEffect, useRef, useState } from 'react';

interface SearchableDropdownProps {
    label?: string;
    id?: string;
    menuItems: IMenuItem[];
    selected?: IMenuItem;
    className?: string;
    placeholder?: string;
    isError?: string | boolean;
    isRequired?: boolean;
    refInputElement?: (inputElement: HTMLInputElement) => void;
    onChange: (item: IMenuItem) => void;
    disabled?: boolean;
    hasError?: boolean;
    showImage?: boolean;
}

export const SearchableDropdown = ({
    label,
    id,
    menuItems,
    selected,
    className,
    placeholder,
    isRequired,
    onChange,
    refInputElement,
    disabled,
    hasError = false,
    showImage = false,
}: SearchableDropdownProps) => {
    const allItems = useRef<HTMLButtonElement[]>([]);
    const [filteredItems, setFilteredItems] = useState(menuItems);
    const [searchInput, setSearchInput] = useState(selected?.label || '');
    const [openMenu, setOpenMenu] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);

    const addItemToRef = (item: HTMLButtonElement | null) => {
        if (item) allItems.current.push(item);
    };

    useEffect(() => {
        setFilteredItems(menuItems);
    }, [menuItems]);

    useEffect(() => {
        setSearchInput(selected?.label ?? '');
    }, [selected?.value, selected?.label]);

    const changeSelect = (item: IMenuItem) => {
        onChange(item);
        setOpenMenu(false);
        validateDropdown();
    };

    const checkOnBlur = (e: React.FocusEvent<HTMLButtonElement, Element>) => {
        if (!e.relatedTarget?.classList.contains('dropdown__menu--item')) {
            setOpenMenu(false);
            validateDropdown();
        }
    };

    const validateDropdown = () => {
        const hasError = isRequired && (!selected || selected.value === undefined || selected.value === null);
        if (hasError) setError(hasError);
    };

    const handleKeyDownChangeItem = (next: number) => {
        const newItem = filteredItems[next];
        allItems.current.forEach(item => {
            if (item.id === newItem.value) item.scrollIntoView();
        });

        onChange(newItem);
        setSearchInput(newItem.label);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        let currentIndex = 0;
        if (selected) currentIndex = filteredItems.findIndex(item => item.value === selected.value);

        if (e.key === 'ArrowUp') {
            if (currentIndex === 0) {
                handleKeyDownChangeItem(filteredItems.length - 1);
            }
            if (currentIndex > 0) {
                handleKeyDownChangeItem(currentIndex - 1);
            }
        }

        if (e.key === 'ArrowDown') {
            if (currentIndex === filteredItems.length - 1) {
                handleKeyDownChangeItem(0);
            }
            if (currentIndex < filteredItems.length - 1) {
                handleKeyDownChangeItem(currentIndex + 1);
            }
        }

        if (e.key === 'Enter') {
            e.preventDefault();
            if (filteredItems[currentIndex]) {
                changeSelect(filteredItems[currentIndex]);
            }
        }

        if (e.key === 'Escape') {
            e.stopPropagation();
            setOpenMenu(false);
        }
    };

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchInput(e.target.value);
        if (!openMenu) setOpenMenu(true);
        const val = e.target.value.toLocaleLowerCase();
        if (val === '') setFilteredItems(menuItems);
        else setFilteredItems(prev => prev.filter(it => it.label.toLocaleLowerCase().includes(val)));
    };

    useEffect(() => {
        if (isRequired) {
            validateDropdown();
        } else {
            setError(false);
        }
    }, [selected, isRequired]);

    return (
        <div className={cc(['dropdown', className, { 'dropdown--opened': searchInput }])}>
            {label && (
                <label htmlFor={id} className="dropdown__label paragraph-4">
                    {label}
                    {isRequired ? <span className="dropdown_is_req">*</span> : ''}
                </label>
            )}
            <button
                className={cc(['dropdown__select dropdown__select--search', { 'dropdown__select--error': hasError || error }])}
                onClick={() => setOpenMenu(!openMenu)}
                onBlur={checkOnBlur}
                onKeyDown={handleKeyDown}
                type="button"
                disabled={disabled}
            >
                <div className={cc(['dropdown__selected-item', openMenu && filteredItems.length > 0 && 'dropdown__selected-item--active'])}>
                    {showImage && selected?.imageUrl && (
                        <img src={selected.imageUrl} alt={selected.label} className="dropdown__selected-item-image" />
                    )}
                    <input
                        className="dropdown__search paragraph-4"
                        value={searchInput}
                        onChange={onInputChange}
                        placeholder={placeholder}
                        ref={refInputElement}
                        disabled={disabled}
                    />
                    {openMenu && filteredItems.length > 0 ? (
                        <img src="/svg/arrow-up.svg" alt="Arrow Down" />
                    ) : (
                        <img src="/svg/arrow-down.svg" alt="Arrow Up" />
                    )}
                </div>
            </button>
            {openMenu && filteredItems.length > 0 && (
                <div className="dropdown__menu">
                    {filteredItems.map(option => (
                        <button
                            type="button"
                            id={option.value.toString()}
                            ref={el => addItemToRef(el)}
                            className={cc([
                                'dropdown__menu--item paragraph-4',
                                selected && selected.value === option.value && 'dropdown__menu--item-active',
                            ])}
                            key={option.value}
                            onClick={() => {
                                changeSelect(option);
                            }}
                        >
                            {showImage && option.imageUrl && <img src={option.imageUrl} alt={option.label} className="dropdown__menu--item-image" />}
                            {option.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};
